import { ArpMediaType, MintStatus, MintStatusModel } from '@arpeggi/shared'
import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { styled } from '../../lib/stitches'
import { useMintStatuses } from '../../queries/mintStatuses'
import { secondsToMilliseconds } from '../../utils/time'
import { Box } from '../core/Components'
import ProgressBar from '../core/ProgressBar'
import Text from '../core/Text/Text'
import Spinner from '../platform/new-explore/components/Spinner'

const MintInProgressBox = styled('div', {
  width: '200px',
  height: '60px',
  backgroundColor: '$gray02',
  borderRadius: '20px',
  padding: '10px 20px',
  transition: 'all 100ms ease',
  variants: {
    clickable: {
      true: {
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-1px)'
        },
        '&:active': {
          transform: 'translateY(1px)'
        }
      }
    }
  }
})

const calculateProgress = (media: MintStatusModel): { message: string; progress: number } => {
  const { mintStatus, createdAt } = media
  const created = new Date(createdAt).valueOf()
  const now = Date.now()
  if (now - created > 1000 * 60 * 60 * 6)
    return { message: `Stuck for ${Math.floor((now - created) / (1000 * 60 * 60))}h`, progress: 50 }

  switch (mintStatus) {
    case MintStatus.Complete:
      return { message: 'Complete', progress: 100 }

    // Song Mint flow
    case MintStatus.Queued:
      return { message: 'Queued', progress: 10 }
    case MintStatus.MintingSong:
      return { message: 'Minting song', progress: 20 }
    case MintStatus.RenderingAndUploadingAudio:
      return { message: 'Rendering audio', progress: 30 }
    case MintStatus.RegisteringStemTokens:
      return { message: 'Registering stems', progress: 50 }
    case MintStatus.RegisteringSongToArp:
      return { message: 'Registering song', progress: 70 }
    case MintStatus.WaitingForSongRegistrationConfirmation:
      return { message: 'Finalizing', progress: 90 }

    // Sound Mint flow
    case MintStatus.MintingAndRegisteringSoundToken:
      return { message: 'Minting sound', progress: 50 }
    case MintStatus.WaitingForSoundMintConfirmation:
      return { message: 'Finalizing sound', progress: 90 }

    default:
      return { message: 'Unexpected state', progress: 100 }
  }
}

const MintsInProgressTable: FC<{ walletAddress: string }> = ({ walletAddress }) => {
  const { data, isFetching } = useMintStatuses({
    params: {
      completed: false,
      walletAddress
    },
    options: {
      refetchInterval: secondsToMilliseconds(5)
    }
  })
  const navigate = useNavigate()

  const flattenData = data?.pages?.flatMap((page) => page.data)

  if (!data || flattenData.length === 0) {
    return null
  }

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        marginBottom: '16px'
      }}
    >
      <Box css={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <Text size="18">Mints in progress</Text>
        {isFetching && <Spinner css={{ width: '16px', height: '16px' }} />}
      </Box>

      <Box
        css={{
          flex: 1,
          marginTop: '16px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '20px'
        }}
      >
        {data?.pages?.map((page) =>
          page.data.map((media, i) => {
            const { progress, message } = calculateProgress(media)
            const mediaType = media.metadata.mediaType as ArpMediaType
            const isSong = mediaType === ArpMediaType.SONG
            return (
              <MintInProgressBox
                clickable={isSong}
                onClick={() => isSong && navigate(`/song-preview/${media.jobId}`)}
              >
                <Text
                  size="12"
                  fontWeight="semibold"
                  css={{
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    marginBottom: '5px'
                  }}
                >
                  {media.metadata.title}
                </Text>
                <Box css={{ display: 'flex', alignItems: 'center' }}>
                  <ProgressBar
                    progress={progress}
                    error={message === 'Error occured' || message.includes('Stuck for')}
                  />
                  <Text size="10" css={{ color: '$gray10' }}>
                    {message}
                  </Text>
                </Box>
              </MintInProgressBox>
            )
          })
        )}
      </Box>
    </Box>
  )
}

export default MintsInProgressTable
